import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Voicemail, Phone, Save, Server, Mic, Repeat } from 'lucide-react';
import { getToken } from '../lib/api';
import { useToast } from '../components/Toast';
import VoiceGreetingPanel from '../components/VoiceGreetingPanel';
import AudioPicker from '../components/AudioPicker';

const auth = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });
const fJson = async (url: string, init: RequestInit = {}) => {
  const r = await fetch(url, { ...init, headers: { ...auth().headers, ...(init.headers || {}) } });
  if (!r.ok) throw new Error(await r.text());
  return r.json();
};

type AuthType = 'ip' | 'credentials';
type PromptMode = 'loop' | 'once';

type Trunk = {
  name: string | null;
  host: string | null;
  username: string | null;
  auth_type: AuthType;
  configured: boolean;
};

type RemoteVm = {
  did: string | null;
  enabled: boolean;
  greeting_prompt_uuid: string | null;
  prompt_mode: PromptMode;
  loop_pause_seconds: number;
  no_input_timeout_seconds: number;
  max_loops: number;
  trunk: Trunk | null;
  updated_at: string | null;
};

// Local, editable copy of the trunk. Password is never returned by GET; we only
// send it when the operator types a new one (blank = keep the existing secret).
type TrunkForm = { name: string; host: string; username: string; password: string; auth_type: AuthType };

const clampMin1 = (v: string) => { const n = parseInt(v, 10); return Number.isFinite(n) && n >= 1 ? n : 1; };

export default function RemoteVoicemail() {
  const qc = useQueryClient();
  const toast = useToast();

  const [did, setDid] = useState('');
  const [enabled, setEnabled] = useState(false);
  const [greetingPromptUuid, setGreetingPromptUuid] = useState('');
  const [promptMode, setPromptMode] = useState<PromptMode>('loop');
  const [noInputTimeoutSeconds, setNoInputTimeoutSeconds] = useState(5);
  const [loopPauseSeconds, setLoopPauseSeconds] = useState(2);
  const [maxLoops, setMaxLoops] = useState(3);
  const [trunk, setTrunk] = useState<TrunkForm>({ name: '', host: '', username: '', password: '', auth_type: 'credentials' });
  const [trunkConfigured, setTrunkConfigured] = useState(false);
  const [err, setErr] = useState('');

  const cfg = useQuery<RemoteVm>({ queryKey: ['remote-vm'], queryFn: () => fJson('/api/remote-vm') });

  // Pull the server state into local editable state whenever it (re)loads.
  useEffect(() => {
    if (!cfg.data) return;
    const d = cfg.data;
    setDid(d.did || '');
    setEnabled(!!d.enabled);
    setGreetingPromptUuid(d.greeting_prompt_uuid || '');
    setPromptMode(d.prompt_mode === 'once' ? 'once' : 'loop');
    setNoInputTimeoutSeconds(d.no_input_timeout_seconds ?? 5);
    setLoopPauseSeconds(d.loop_pause_seconds ?? 2);
    setMaxLoops(d.max_loops ?? 3);
    setTrunk({
      name: d.trunk?.name || '',
      host: d.trunk?.host || '',
      username: d.trunk?.username || '',
      password: '',
      auth_type: d.trunk?.auth_type === 'ip' ? 'ip' : 'credentials',
    });
    setTrunkConfigured(!!d.trunk?.configured);
  }, [cfg.data]);

  const setTrunkField = (k: keyof TrunkForm, v: string) => { setTrunk(t => ({ ...t, [k]: v })); if (err) setErr(''); };

  const save = useMutation<RemoteVm>({
    mutationFn: () => fJson('/api/remote-vm', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        did: did.trim(),
        enabled,
        greeting_prompt_uuid: greetingPromptUuid || null,
        prompt_mode: promptMode,
        loop_pause_seconds: loopPauseSeconds,
        no_input_timeout_seconds: noInputTimeoutSeconds,
        max_loops: maxLoops,
        trunk: {
          name: trunk.name.trim() || null,
          host: trunk.host.trim() || null,
          username: trunk.username.trim() || null,
          // Omit when blank so the backend keeps the existing password.
          password: trunk.password ? trunk.password : undefined,
          auth_type: trunk.auth_type,
        },
      }),
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['remote-vm'] }); toast('success', 'Remote voicemail settings saved'); },
    onError: (e: any) => toast('error', e.message),
  });

  const onSave = () => {
    setErr('');
    // A number is required before the feature can be turned on.
    if (enabled && !did.trim()) {
      setErr('Enter a voicemail access number before enabling.');
      return;
    }
    // And the DID has to arrive on a trunk, or nobody can reach the prompt.
    if (enabled && !trunk.host.trim()) {
      setErr('Configure the SIP trunk (SIP server / host) before enabling.');
      return;
    }
    save.mutate();
  };

  const updatedAt = cfg.data?.updated_at ? new Date(cfg.data.updated_at).toLocaleString() : null;

  return (
    <div className="space-y-6 max-w-3xl">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold flex items-center gap-2"><Voicemail size={22} /> Remote Voicemail Access</h1>
        <button onClick={onSave} disabled={save.isPending} className="btn-primary inline-flex items-center gap-2">
          <Save size={14} /> {save.isPending ? 'Saving…' : 'Save'}
        </button>
      </div>
      <p className="text-sm text-textSecondary">
        A single number your users can call from anywhere to check voicemail. Callers enter their extension and PIN — Stratus finds the right department automatically (extensions are unique across all departments) and opens their mailbox using FreeSWITCH's built-in prompts.
      </p>

      {cfg.isLoading && <div className="card p-4 text-sm text-textSecondary">Loading…</div>}
      {cfg.isError && (
        <div className="card p-4 text-sm text-danger">
          Couldn't load settings: {(cfg.error as any)?.message || 'unknown error'}
        </div>
      )}

      {!cfg.isLoading && !cfg.isError && (
        <>
          {/* 1 — SIP TRUNK */}
          <div className="card p-4 space-y-4">
            <div className="font-medium flex items-center gap-2"><Server size={16} /> SIP trunk</div>
            <p className="text-xs text-textSecondary">The trunk that carries your voicemail access number.</p>

            <div>
              <label className="block text-sm text-textSecondary mb-1">Trunk name</label>
              <input className="input w-full" value={trunk.name} onChange={e => setTrunkField('name', e.target.value)} placeholder="remote-voicemail" />
            </div>

            <div>
              <label className="block text-sm text-textSecondary mb-1">Authentication</label>
              <select className="input w-full" value={trunk.auth_type} onChange={e => setTrunkField('auth_type', e.target.value)}>
                <option value="credentials">Credentials (register)</option>
                <option value="ip">IP address</option>
              </select>
            </div>

            <div>
              <label className="block text-sm text-textSecondary mb-1">SIP server / host</label>
              <input className="input w-full font-mono text-xs" value={trunk.host} onChange={e => setTrunkField('host', e.target.value)} placeholder="sip.provider.com" />
            </div>

            {trunk.auth_type === 'credentials' && (
              <>
                <div>
                  <label className="block text-sm text-textSecondary mb-1">Username</label>
                  <input className="input w-full font-mono text-xs" value={trunk.username} onChange={e => setTrunkField('username', e.target.value)} placeholder="account username" />
                </div>
                <div>
                  <label className="block text-sm text-textSecondary mb-1">Password</label>
                  <input type="password" className="input w-full" value={trunk.password} onChange={e => setTrunkField('password', e.target.value)}
                    placeholder={trunkConfigured ? 'leave blank to keep current' : 'account password'} />
                </div>
              </>
            )}

            <p className="text-xs text-textSecondary">Stratus creates a dedicated trunk and loads it into FreeSWITCH. Point your provider's number at this system.</p>
          </div>

          {/* 2 — ACCESS NUMBER */}
          <div className="card p-4 space-y-4">
            <div className="font-medium flex items-center gap-2"><Phone size={16} /> Access number</div>

            <div>
              <label className="block text-sm text-textSecondary mb-1">Voicemail access number (DID)</label>
              <input
                className="input w-full font-mono"
                value={did}
                onChange={e => { setDid(e.target.value); if (err) setErr(''); }}
                placeholder="+19895551234"
              />
              <p className="text-xs text-textSecondary mt-1">The number users call to check voicemail. Use a dedicated spare number.</p>
            </div>

            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={enabled} onChange={e => { setEnabled(e.target.checked); if (err) setErr(''); }} />
              Enabled <span className="text-textSecondary">(callers can dial this number to check voicemail)</span>
            </label>

            {err && <p className="text-xs text-danger">{err}</p>}
          </div>

          {/* 3 — GREETING (Navi) */}
          <div className="card p-4 space-y-4">
            <div className="font-medium flex items-center gap-2"><Mic size={16} /> Greeting</div>
            <p className="text-xs text-textSecondary">Plays before the system asks for the extension and PIN. Optional — leave unset to use only the system prompts.</p>

            <VoiceGreetingPanel onSaved={() => qc.invalidateQueries({ queryKey: ['custom-prompts'] })} />

            <div>
              <label className="block text-sm text-textSecondary mb-1">Greeting to play</label>
              <AudioPicker value={greetingPromptUuid} onChange={setGreetingPromptUuid} />
            </div>
          </div>

          {/* 4 — NO-RESPONSE BEHAVIOR */}
          <div className="card p-4 space-y-4">
            <div className="font-medium flex items-center gap-2"><Repeat size={16} /> If the caller doesn't respond</div>
            <p className="text-xs text-textSecondary">
              When a caller doesn't enter an extension, Stratus can either re-explain the prompt on a loop until they do, or say it once and hang up.
            </p>

            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm">
                <input type="radio" name="prompt_mode" checked={promptMode === 'loop'} onChange={() => setPromptMode('loop')} />
                Loop and re-explain
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input type="radio" name="prompt_mode" checked={promptMode === 'once'} onChange={() => setPromptMode('once')} />
                Say once, then hang up
              </label>
            </div>

            <div>
              <label className="block text-sm text-textSecondary mb-1">Wait for input (seconds)</label>
              <input type="number" min={1} className="input w-40" value={noInputTimeoutSeconds}
                onChange={e => setNoInputTimeoutSeconds(clampMin1(e.target.value))} />
            </div>

            {promptMode === 'loop' && (
              <>
                <div>
                  <label className="block text-sm text-textSecondary mb-1">Pause between re-explanations (seconds)</label>
                  <input type="number" min={1} className="input w-40" value={loopPauseSeconds}
                    onChange={e => setLoopPauseSeconds(clampMin1(e.target.value))} />
                </div>
                <div>
                  <label className="block text-sm text-textSecondary mb-1">Max re-explanations before hanging up</label>
                  <input type="number" min={1} className="input w-40" value={maxLoops}
                    onChange={e => setMaxLoops(clampMin1(e.target.value))} />
                </div>
              </>
            )}
          </div>

          <div className="flex items-center gap-3">
            <button onClick={onSave} disabled={save.isPending} className="btn-primary inline-flex items-center gap-2">
              <Save size={14} /> {save.isPending ? 'Saving…' : 'Save'}
            </button>
            {save.isSuccess && !save.isPending && <span className="text-xs text-success">Saved.</span>}
            {updatedAt && <span className="text-xs text-textSecondary">Last updated {updatedAt}</span>}
          </div>
        </>
      )}
    </div>
  );
}
